import React, { FC } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import {
  InfoPlaceholderSelector,
  MacronutrientBreakdown,
  NutritionAnalysisInfo,
  NutritionAnalysisLayout,
  PanelTextPicker
} from '../components'
import useCalculateRange from '../hooks/useCalculateRange'
import useDateRangePanel from '../hooks/useDateRangePanel'

import { DateRangeModal } from 'Components'
import { DateRangeString, MacronutrientSet, ReduxStoreState, analyticsNutritionsDateRanges } from 'Models'
import { registrationDateSelector } from 'ReduxStore/coreBackendUser'
import {
  analyticsCacheRevisionSelector,
  fetchNutritionSummaryAction,
  isNutritionSummaryLoadingSelector,
  nutritionSummaryRecommendedSelector,
  nutritionSummaryTrackedSelector
} from 'ReduxStore/myAnalytics'
import { analyticsNutritionsDateRangeSelector, updateUserSettingsAction } from 'ReduxStore/userSettings'

type SelectorProps = {
  analyticsNutritionsDateRange?: DateRangeString
  analyticsCacheRevision: number
  nutritionSummaryRecommended?: MacronutrientSet
  nutritionSummaryTracked?: MacronutrientSet
  registrationDate: Date
  isNutritionSummaryLoading: boolean
}

const mapStateToProps = createStructuredSelector<ReduxStoreState, SelectorProps>({
  analyticsNutritionsDateRange: analyticsNutritionsDateRangeSelector,
  analyticsCacheRevision: analyticsCacheRevisionSelector,
  nutritionSummaryRecommended: nutritionSummaryRecommendedSelector,
  nutritionSummaryTracked: nutritionSummaryTrackedSelector,
  registrationDate: registrationDateSelector,
  isNutritionSummaryLoading: isNutritionSummaryLoadingSelector
})

const mapDispatchToProps = {
  fetchNutritionSummary: fetchNutritionSummaryAction,
  updateUserSettings: updateUserSettingsAction
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const MacronutrientBreakdownPage: FC<PropsFromRedux> = ({
  fetchNutritionSummary,
  updateUserSettings,
  analyticsNutritionsDateRange,
  analyticsCacheRevision,
  nutritionSummaryRecommended,
  nutritionSummaryTracked,
  registrationDate,
  isNutritionSummaryLoading
}: PropsFromRedux) => {
  const calculateRange = useCalculateRange()

  const nutritionsPanel = useDateRangePanel({
    analyticsDateRange: analyticsNutritionsDateRange,
    settingsKey: 'analyticsNutritionsDateRange',
    calculateRange,
    fetchFn: fetchNutritionSummary,
    refreshKey: analyticsCacheRevision,
    updateUserSettings
  })

  const hasNutritionData =
    (nutritionSummaryRecommended && Object.keys(nutritionSummaryRecommended).length > 0) ||
    (nutritionSummaryTracked && Object.keys(nutritionSummaryTracked).length > 0)

  return (
    <NutritionAnalysisLayout>
      <PanelTextPicker
        onChange={nutritionsPanel.handleChange}
        onItemClick={nutritionsPanel.handleItemClick}
        items={analyticsNutritionsDateRanges}
        selectedKey={analyticsNutritionsDateRange}
        title="Nährwertdiagramm"
        rightOfTitle={<NutritionAnalysisInfo />}
        labelOverrides={nutritionsPanel.labelOverrides}
      >
        {hasNutritionData || isNutritionSummaryLoading ? (
          <MacronutrientBreakdown
            nutritionSummaryRecommended={nutritionSummaryRecommended}
            nutritionSummaryTracked={nutritionSummaryTracked}
            loading={isNutritionSummaryLoading}
          />
        ) : (
          <InfoPlaceholderSelector dateRange={analyticsNutritionsDateRange} />
        )}
      </PanelTextPicker>

      <DateRangeModal
        isOpen={nutritionsPanel.showPicker}
        onClose={() => nutritionsPanel.setShowPicker(false)}
        onApply={nutritionsPanel.handleApply}
        minimumDate={registrationDate}
      />
    </NutritionAnalysisLayout>
  )
}

export default connector(MacronutrientBreakdownPage)
